import { useCallback } from 'react';
import { originGroupsApi } from '@/api/originGroups';
import { useResource } from '@/hooks/useResource';
import type {
  ConnectionTestResponse,
  CreateOriginGroupRequest,
  OriginGroup,
  OriginGroupFilters,
  OriginGroupListResponse,
  UpdateOriginGroupRequest,
} from '@/types/originGroup';

/**
 * Page size used when listing origin groups (the UI does not paginate)
 */
const ORIGIN_GROUP_LIMIT = 100;

/**
 * Origin group list + operations
 */
export function useOriginGroups(filters: OriginGroupFilters = {}) {
  const { health_status, enable_tls, search } = filters;

  const fetcher = useCallback(
    (signal: AbortSignal) =>
      originGroupsApi.getAll({ health_status, enable_tls, search }, 1, ORIGIN_GROUP_LIMIT, { signal }),
    [health_status, enable_tls, search]
  );

  const resource = useResource<OriginGroupListResponse>(fetcher, {
    errorMessage: 'Failed to load origin groups',
  });
  const { setData, reload } = resource;

  const createOriginGroup = useCallback(
    async (data: CreateOriginGroupRequest): Promise<OriginGroup> => {
      const created = await originGroupsApi.create(data);
      void reload();
      return created;
    },
    [reload]
  );

  const updateOriginGroup = useCallback(
    async (id: string, data: UpdateOriginGroupRequest): Promise<OriginGroup> => {
      const updated = await originGroupsApi.update(id, data);
      setData((prev) =>
        prev && {
          ...prev,
          origin_groups: prev.origin_groups.map((og) => (og.id === id ? updated : og)),
        }
      );
      return updated;
    },
    [setData]
  );

  const deleteOriginGroup = useCallback(
    async (id: string): Promise<void> => {
      await originGroupsApi.delete(id);
      setData(
        (prev) =>
          prev && {
            origin_groups: prev.origin_groups.filter((og) => og.id !== id),
            total: Math.max(0, prev.total - 1),
          }
      );
    },
    [setData]
  );

  const testConnection = useCallback(
    (id: string): Promise<ConnectionTestResponse> => originGroupsApi.testConnection(id),
    []
  );

  return {
    originGroups: resource.data?.origin_groups ?? [],
    total: resource.data?.total ?? 0,
    isLoading: resource.isLoading,
    error: resource.error,
    reload,
    createOriginGroup,
    updateOriginGroup,
    deleteOriginGroup,
    testConnection,
  };
}
